import { getApiEndpoint } from "../config/config";

// Manual revenue: flat dollar figures recorded for a rep by hand (one at a
// time or from a CSV) that the pay engine adds to their FieldRoutes
// production. A bulk figure can be carved into dated slices so time-boxed
// overrides see the right amount. Talks to /v1/manual-revenue/*.

const request = async (path, jwt, { method = "GET", body, formData } = {}) => {
  const response = await fetch(getApiEndpoint(path), {
    method,
    headers: {
      ...(formData ? {} : { "Content-Type": "application/json" }),
      Authorization: `Bearer ${jwt}`,
    },
    ...(formData ? { body: formData } : {}),
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.message || `HTTP ${response.status}`);
  }
  return payload.data;
};

export const manualRevenueService = {
  // Top-level entries (allocations nested) plus per-rep totals for the
  // selling year (default: the active one); repId narrows to one rep.
  list: (jwt, { sellingYearId, repId } = {}) => {
    const params = new URLSearchParams();
    if (sellingYearId != null) params.set("selling_year_id", sellingYearId);
    if (repId != null) params.set("rep_id", repId);
    const query = params.toString();
    return request(
      `/v1/manual-revenue/entries${query ? `?${query}` : ""}`,
      jwt
    );
  },

  // One flat figure for a rep. Dates are optional (both or neither).
  create: (entry, jwt) =>
    request("/v1/manual-revenue/entries", jwt, { method: "POST", body: entry }),

  // "Of entry N's revenue, this much happened between these dates."
  allocate: (entryId, allocation, jwt) =>
    request(`/v1/manual-revenue/entries/${entryId}/allocations`, jwt, {
      method: "POST",
      body: allocation,
    }),

  update: (entryId, changes, jwt) =>
    request(`/v1/manual-revenue/entries/${entryId}`, jwt, {
      method: "PUT",
      body: changes,
    }),

  remove: (entryId, jwt) =>
    request(`/v1/manual-revenue/entries/${entryId}`, jwt, { method: "DELETE" }),

  // Flat CSV: Rep ID or Email, Amount, and optional Accounts / Start Date /
  // End Date / Note / Parent Entry ID. All-or-nothing; a bad file comes back
  // as an error listing every problem row.
  importCsv: (file, jwt, sellingYearId = null) => {
    const formData = new FormData();
    formData.append("file", file);
    if (sellingYearId != null) {
      formData.append("selling_year_id", String(sellingYearId));
    }
    return request("/v1/manual-revenue/import-csv", jwt, {
      method: "POST",
      formData,
    });
  },

  // The starter CSV, as text, so the page can hand it to the browser.
  template: async jwt => {
    const response = await fetch(
      getApiEndpoint("/v1/manual-revenue/import-template"),
      { headers: { Authorization: `Bearer ${jwt}` } }
    );
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.text();
  },
};
